import { z } from 'zod';
import { MemberSchema, type Member } from '../../domain/entities/member.ts';
import type { ClockPort } from '../../ports/clock-port.ts';
import type { DataPort } from '../../ports/data-port.ts';
import { expectUnchanged, updateWrite } from '../../ports/batch.ts';

/**
 * Which jobs a member may see (P8-14).
 *
 * The one thing that decides it, and nothing until now could write it.
 * `firestore.rules` reads `jobAccess == 'all' || jobIds.hasAny([jobId])`,
 * and `jobIds` was only ever set by `acceptInvite` copying it from the
 * invite — which the invite form never filled in. So every client and
 * every sub accepted an invite carrying an empty list and could see no
 * job at all, which looks like a misconfiguration rather than a missing
 * control.
 *
 * **An external member is never given `all`.** The form does not offer
 * it and this refuses it, because the two mistakes are not symmetrical:
 * a client who sees too few jobs rings up, and a client who sees every
 * job in the company has seen other people's money. `acceptInvite`
 * already decides `all` for internal and `assigned` for external, and
 * this will not quietly undo that.
 *
 * **Guarded on the member the caller loaded.** Two administrators with
 * the list open, one adds a job, the other saves a minute later from a
 * snapshot taken before — and the second silently takes the first's
 * grant away. `expectUnchanged` turns that into a refusal somebody can
 * see, which is the same reasoning `updateJob` sets out at length.
 */
export class MemberJobsError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MemberJobsError';
  }
}

export const SetMemberJobsInputSchema = z.object({
  /**
   * `all` is internal-only, refused below for anyone else. It is in the
   * schema because an administrator moving an internal member between
   * "every job" and a list is a legitimate change.
   */
  jobAccess: z.enum(['all', 'assigned']),
  jobIds: z.array(z.string().min(1)).max(500),
});
export type SetMemberJobsInput = z.infer<typeof SetMemberJobsInputSchema>;

export interface MemberJobsContext {
  readonly data: DataPort;
  readonly clock: ClockPort;
  readonly companyId: string;
  readonly uid: string;
}

export async function setMemberJobs(
  context: MemberJobsContext,
  member: Member,
  input: SetMemberJobsInput,
): Promise<Member> {
  const parsed = SetMemberJobsInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new MemberJobsError(parsed.error.issues[0]!.message);
  }

  if (parsed.data.jobAccess === 'all' && member.kind !== 'internal') {
    throw new MemberJobsError(
      'A client or a subcontractor is given jobs one at a time, never the whole company.',
    );
  }

  /**
   * Duplicates removed and order left alone otherwise.
   *
   * `hasAny` does not care, but a list that grows a second copy of the
   * same id every time somebody re-ticks a box is a list that eventually
   * hits the 500 cap for no reason.
   */
  const jobIds = [...new Set(parsed.data.jobIds)];

  /**
   * An empty list is **allowed**, and deliberately: it is how somebody is
   * taken off every job without removing their account. Refusing it would
   * leave no way to do that short of deactivating them.
   *
   * It is also the one change that can silently lock a person out, so
   * `jobAccessSummary` says "they can sign in and will see nothing"
   * rather than leaving them to find out.
   */

  const at = context.clock.now().toISOString();
  const next: Member = {
    ...member,
    jobAccess: parsed.data.jobAccess,
    // `all` means the list is not consulted, so it is cleared rather than
    // left to rot: a stale list that reappears if somebody switches back
    // to `assigned` is a grant nobody made today.
    jobIds: parsed.data.jobAccess === 'all' ? [] : jobIds,
    updatedAt: at,
    updatedBy: context.uid,
  };

  const checked = MemberSchema.safeParse(next);
  if (!checked.success) throw new MemberJobsError(checked.error.issues[0]!.message);

  await context.data.commit([
    updateWrite<Member>(
      { companyId: context.companyId },
      'members',
      member.id,
      {
        jobAccess: checked.data.jobAccess,
        jobIds: checked.data.jobIds,
        updatedAt: at,
        updatedBy: context.uid,
      },
      expectUnchanged(member),
    ),
  ]);

  return checked.data;
}

/**
 * What a job list means in a sentence, for a screen to show.
 *
 * Here rather than in the component because the invite form and the
 * member row both say it, and two copies is two chances for one of them
 * to describe a grant it is not making.
 */
export function jobAccessSummary(
  member: Pick<Member, 'kind' | 'jobAccess' | 'jobIds'>,
  nameOf: (jobId: string) => string | undefined,
): string {
  if (member.jobAccess === 'all') return 'Every job in the company';
  if (member.jobIds.length === 0) {
    return member.kind === 'internal'
      ? 'No jobs yet'
      : 'No jobs — they can sign in and will see nothing';
  }
  const names = member.jobIds.map((id) => nameOf(id) ?? 'a job no longer here');
  if (names.length <= 2) return names.join(' and ');
  return `${names.slice(0, 2).join(', ')} and ${names.length - 2} more`;
}
